/**
 * Per-tenant school access-denial messages (SchoolService — `/MessageSettings`).
 *
 * The SchoolService serializes PascalCase across the board, so these DTOs are
 * PascalCase too — 1:1 with the wire format, no mappers. `GET` returns the
 * tenant's messages (creating the row with service defaults on first read);
 * `PUT` has patch semantics — only the provided fields are updated — and echoes
 * the full updated record.
 */

import { defaultLanguage } from '@shared/utils/language-loader';

/** GET/PUT `/MessageSettings` response. */
export interface SchoolMessageSettings {
  MessageSettingsId: string;
  PersonNotFound: string;
  PersonTypeUnsupported: string;
  NoCurrentAcademicYear: string;
  NoActiveEnrollment: string;
  OutsideAcademicPeriod: string;
  NotSchoolDay: string;
  OutsideSchedule: string;
  PersonInactive: string;
  /** Refusal by the guardian axis (040): the tie has no pickup authorization. */
  PickupNotAuthorized: string;
  /** Refusal by the guardian axis (040): the tie's authorization is undefined. */
  PickupAuthorizationUndefined: string;
  /** Refusal by the guardian axis (040): the conditional authorization expired. */
  PickupValidUntilOnExpired: string;
  LatenessLimitReached: string;
  AbsenceLimitReached: string;
  /** Greeting shown by the gate display when an entry is allowed. */
  EntryAllowed: string;
  /** Greeting shown by the gate display when an exit is allowed. */
  ExitAllowed: string;
}

/** PUT `/MessageSettings` body (patch semantics — omitted fields are kept). */
export type SchoolMessageSettingsRequest = Partial<SchoolMessageSettings> &
  Pick<SchoolMessageSettings, 'MessageSettingsId'>;

/** Editable message fields (everything except the id). */
export type TSchoolMessageField = Exclude<keyof SchoolMessageSettings, 'MessageSettingsId'>;

export interface SchoolMessageSettingsState {
  /** Messages from the last successful load/save (null until loaded). */
  messageSettings: SchoolMessageSettings | null;
}

export const initialSchoolMessageSettingsState: SchoolMessageSettingsState = {
  messageSettings: null,
};

/**
 * Per-language defaults for the "restore default" button. `pt-BR` mirrors the
 * SchoolService entity defaults verbatim; the other languages are the UI-side
 * translations of the same messages.
 */
const SCHOOL_DEFAULT_MESSAGES: Record<string, Record<TSchoolMessageField, string>> = {
  'pt-BR': {
    PersonNotFound: 'Pessoa não encontrada.',
    PersonTypeUnsupported: 'Tipo de cadastro não autorizado para acesso.',
    NoCurrentAcademicYear: 'Não há ano letivo vigente.',
    NoActiveEnrollment: 'Nenhuma matrícula ativa encontrada.',
    OutsideAcademicPeriod: 'Fora do período letivo.',
    NotSchoolDay: 'Hoje não é dia letivo.',
    OutsideSchedule: 'Fora do horário de aula.',
    PersonInactive: 'Cadastro inativo.',
    PickupNotAuthorized: 'Retirada não autorizada.',
    PickupAuthorizationUndefined: 'Autorização de retirada não definida.',
    PickupValidUntilOnExpired: 'Autorização de retirada expirada.',
    LatenessLimitReached: 'Entrada não liberada: limite de atrasos atingido. Procure a secretaria.',
    AbsenceLimitReached: 'Entrada não liberada: limite de faltas atingido. Procure a secretaria.',
    EntryAllowed: 'Bem-vindo!',
    ExitAllowed: 'Até logo!',
  },
  'en-US': {
    PersonNotFound: 'Person not found.',
    PersonTypeUnsupported: 'Registration type not authorized for access.',
    NoCurrentAcademicYear: 'There is no current academic year.',
    NoActiveEnrollment: 'No active enrollment found.',
    OutsideAcademicPeriod: 'Outside the academic period.',
    NotSchoolDay: 'Today is not a school day.',
    OutsideSchedule: 'Outside class hours.',
    PersonInactive: 'Inactive registration.',
    PickupNotAuthorized: 'Pickup not authorized.',
    PickupAuthorizationUndefined: 'Pickup authorization not defined.',
    PickupValidUntilOnExpired: 'Pickup authorization expired.',
    LatenessLimitReached: 'Entry not released: lateness limit reached. Please contact the office.',
    AbsenceLimitReached: 'Entry not released: absence limit reached. Please contact the office.',
    EntryAllowed: 'Welcome!',
    ExitAllowed: 'See you soon!',
  },
  'es-ES': {
    PersonNotFound: 'Persona no encontrada.',
    PersonTypeUnsupported: 'Tipo de registro no autorizado para el acceso.',
    NoCurrentAcademicYear: 'No hay año lectivo vigente.',
    NoActiveEnrollment: 'Ninguna matrícula activa encontrada.',
    OutsideAcademicPeriod: 'Fuera del período lectivo.',
    NotSchoolDay: 'Hoy no es día lectivo.',
    OutsideSchedule: 'Fuera del horario de clase.',
    PersonInactive: 'Registro inactivo.',
    PickupNotAuthorized: 'Retirada no autorizada.',
    PickupAuthorizationUndefined: 'Autorización de retirada no definida.',
    PickupValidUntilOnExpired: 'Autorización de retirada expirada.',
    LatenessLimitReached: 'Entrada no liberada: límite de retrasos alcanzado. Acuda a la secretaría.',
    AbsenceLimitReached: 'Entrada no liberada: límite de faltas alcanzado. Acuda a la secretaría.',
    EntryAllowed: '¡Bienvenido!',
    ExitAllowed: '¡Hasta luego!',
  },
};

const SCHOOL_LANGUAGE_MAP: Record<string, string> = {
  'pt': 'pt-BR',
  'pt-BR': 'pt-BR',
  'en': 'en-US',
  'en-US': 'en-US',
  'es': 'es-ES',
  'es-ES': 'es-ES',
};

export const getSchoolDefaultMessages = (
  language = defaultLanguage,
): Record<TSchoolMessageField, string> => {
  const mappedLang = SCHOOL_LANGUAGE_MAP[language || defaultLanguage] || defaultLanguage;
  return SCHOOL_DEFAULT_MESSAGES[mappedLang] || SCHOOL_DEFAULT_MESSAGES[defaultLanguage];
};
